<template>
  <canvas ref="canvasRef" class="particle-canvas"></canvas>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'

interface Props {
  /** Number of particles (default: 60) */
  count?: number
  /** Max connection distance in px (default: 140) */
  connectionDistance?: number
  /** Particle color — CSS rgba string (default: uses accent-primary blue) */
  color?: string
  /** Canvas opacity (default: 0.3) */
  opacity?: number
}

const props = withDefaults(defineProps<Props>(), {
  count: 60,
  connectionDistance: 140,
  color: 'rgba(74, 158, 255, {alpha})',
  opacity: 0.3,
})

const canvasRef = ref<HTMLCanvasElement | null>(null)
let animationId: number | null = null
let particles: Array<{ x: number; y: number; vx: number; vy: number }> = []
let resizeObserver: ResizeObserver | null = null

function getColor(alpha: number): string {
  return props.color.replace('{alpha}', String(alpha))
}

function initCanvas() {
  const canvas = canvasRef.value
  if (!canvas || !canvas.parentElement) return

  const parent = canvas.parentElement
  canvas.width = parent.offsetWidth
  canvas.height = parent.offsetHeight

  particles = []
  for (let i = 0; i < props.count; i++) {
    particles.push({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
    })
  }
}

function animate() {
  const canvas = canvasRef.value
  if (!canvas) return

  const ctx = canvas.getContext('2d')
  if (!ctx) return

  ctx.clearRect(0, 0, canvas.width, canvas.height)

  particles.forEach((p) => {
    p.x += p.vx
    p.y += p.vy
    if (p.x < 0 || p.x > canvas.width) p.vx *= -1
    if (p.y < 0 || p.y > canvas.height) p.vy *= -1
  })

  ctx.strokeStyle = getColor(0.25)
  ctx.lineWidth = 1

  for (let i = 0; i < particles.length; i++) {
    for (let j = i + 1; j < particles.length; j++) {
      const pi = particles[i]
      const pj = particles[j]
      if (!pi || !pj) continue

      const dx = pi.x - pj.x
      const dy = pi.y - pj.y
      const dist = Math.sqrt(dx * dx + dy * dy)

      if (dist < props.connectionDistance) {
        ctx.beginPath()
        ctx.moveTo(pi.x, pi.y)
        ctx.lineTo(pj.x, pj.y)
        ctx.globalAlpha = 1 - dist / props.connectionDistance
        ctx.stroke()
      }
    }
  }

  ctx.fillStyle = getColor(0.5)
  ctx.globalAlpha = 1
  particles.forEach((p) => {
    ctx.beginPath()
    ctx.arc(p.x, p.y, 2, 0, Math.PI * 2)
    ctx.fill()
  })

  animationId = requestAnimationFrame(animate)
}

function handleResize() {
  initCanvas()
}

onMounted(() => {
  initCanvas()
  animate()

  if (canvasRef.value?.parentElement) {
    resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(canvasRef.value.parentElement)
  }
})

onBeforeUnmount(() => {
  if (animationId) cancelAnimationFrame(animationId)
  if (resizeObserver) resizeObserver.disconnect()
})
</script>

<style scoped>
.particle-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  opacity: v-bind(opacity);
}
</style>
